import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Upload, FileText, ImageIcon, X, CheckCircle, Loader2, Trash2, Eye, Clock, AlertCircle
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface Alumno {
  id: number;
  numeroIdentificacionUnico: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  estatus: string;
}

interface PendingInvoice {
  id: number;
  invoiceNumber: string;
  total: string;
  status: string;
}

interface PaymentProof {
  id: number;
  alumnoId: number;
  archivoNombre: string;
  archivoTipo: string;
  archivoTamano: number;
  referencia: string | null;
  invoiceId: number | null;
  invoiceNumber: string | null;
  status: 'pendiente' | 'validado' | 'rechazado';
  notasAdmin: string | null;
  aiFlagged: boolean;
  aiReason: string | null;
  createdAt: string;
  alumnoNui: string;
  alumnoNombre: string;
  alumnoApellidoPaterno: string;
}

const STATUS_CONFIG = {
  pendiente: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
  validado: { label: 'Validado', className: 'bg-green-100 text-green-800' },
  rechazado: { label: 'Rechazado', className: 'bg-red-100 text-red-800' },
};

const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileTypeIcon({ tipo }: { tipo: string }) {
  if (tipo === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
  return <ImageIcon className="w-5 h-5 text-blue-500" />;
}

export default function SubirComprobante() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedAlumnoId, setSelectedAlumnoId] = useState<number | null>(null);
  const [referencia, setReferencia] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  const userPerms: string[] = (user as any)?.permissions || [];
  const isAdmin = userPerms.includes('payment-proofs:list-all');

  // Load alumnos list for admin selector
  const { data: alumnosData } = useQuery({
    queryKey: ['alumnos-simple'],
    queryFn: async () => {
      const r = await fetch('/api/alumnos?limit=500&includeProspectos=true', { credentials: 'include' });
      if (!r.ok) throw new Error('Error');
      return r.json();
    },
    enabled: isAdmin,
  });

  const alumnoOptions = (alumnosData?.alumnos || alumnosData?.data || []).map((a: Alumno) => ({
    value: a.id,
    label: `${a.numeroIdentificacionUnico} — ${a.apellidoPaterno} ${a.nombre}`,
  }));

  // Load pending invoices for selected alumno
  const { data: pendingInvoices = [] } = useQuery<PendingInvoice[]>({
    queryKey: ['pending-invoices', selectedAlumnoId],
    queryFn: async () => {
      if (!selectedAlumnoId) return [];
      const r = await fetch(`/api/payment-proofs/pending-invoices/${selectedAlumnoId}`, { credentials: 'include' });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!selectedAlumnoId,
  });

  // Load own proofs for the selected alumno (or own proofs for student)
  const { data: myProofs = [], isLoading: loadingProofs } = useQuery<PaymentProof[]>({
    queryKey: ['my-payment-proofs', selectedAlumnoId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (isAdmin && selectedAlumnoId) params.set('alumnoId', String(selectedAlumnoId));
      const r = await fetch(`/api/payment-proofs?${params}`, { credentials: 'include' });
      if (!r.ok) return [];
      return r.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error('Sin archivo');
      const alumnoId = isAdmin ? selectedAlumnoId : (user as any)?.alumnoId;
      if (!alumnoId) throw new Error('Sin alumno seleccionado');

      const fd = new FormData();
      fd.append('archivo', selectedFile);
      fd.append('alumnoId', String(alumnoId));
      if (referencia.trim()) fd.append('referencia', referencia.trim());
      if (selectedInvoiceId) fd.append('invoiceId', String(selectedInvoiceId));

      const r = await fetch('/api/payment-proofs/upload', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al subir');
      }
      return r.json();
    },
    onSuccess: (data: any) => {
      if (data?.alumnoStatusChanged?.newStatus === 'aspirante') {
        toast({
          title: '✅ Comprobante enviado — Estatus actualizado',
          description: 'Tu comprobante fue recibido. Tu estatus cambió a ASPIRANTE mientras el equipo revisa tu pago.',
        });
      } else if (data?.proof?.aiFlagged) {
        toast({
          title: 'Comprobante recibido',
          description: 'Tu comprobante fue recibido y quedará en revisión manual por nuestro equipo.',
        });
      } else {
        toast({ title: 'Comprobante enviado', description: 'Tu comprobante de pago fue recibido y está en revisión.' });
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      setReferencia('');
      setSelectedInvoiceId(null);
      queryClient.invalidateQueries({ queryKey: ['my-payment-proofs'] });
      queryClient.invalidateQueries({ queryKey: ['alumnos-simple'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (proofId: number) => {
      const r = await fetch(`/api/payment-proofs/${proofId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al eliminar');
      }
    },
    onSuccess: () => {
      toast({ title: 'Eliminado', description: 'Comprobante eliminado.' });
      queryClient.invalidateQueries({ queryKey: ['my-payment-proofs'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const handleFile = (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: 'Archivo muy grande', description: 'El máximo permitido es 20 MB.', variant: 'destructive' });
      return;
    }
    uploadMutation.reset(); // clear previous error when user picks a new file
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const invoiceOptions = pendingInvoices.map((inv) => ({
    value: inv.id,
    label: `${inv.invoiceNumber} — $${parseFloat(inv.total).toFixed(2)}`,
  }));

  const canUpload = selectedFile && (!isAdmin || selectedAlumnoId);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Subir Comprobante de Pago</h1>
        <p className="text-gray-500 mt-1">Adjunta tu comprobante para que el equipo administrativo lo verifique y aplique tu pago.</p>
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-6 space-y-5">
        {/* Alumno selector (admin only) */}
        {isAdmin && (
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Alumno</Label>
            <SearchableSelect
              options={alumnoOptions}
              value={selectedAlumnoId}
              onChange={(v) => { setSelectedAlumnoId(v ? Number(v) : null); setSelectedInvoiceId(null); }}
              placeholder="Buscar alumno por NUI o nombre..."
            />
          </div>
        )}

        {/* Invoice linker */}
        {(selectedAlumnoId || !isAdmin) && pendingInvoices.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
              Cuenta por cobrar (opcional)
            </Label>
            <SearchableSelect
              options={invoiceOptions}
              value={selectedInvoiceId}
              onChange={(v) => setSelectedInvoiceId(v ? Number(v) : null)}
              placeholder="Selecciona la factura a la que aplica..."
            />
          </div>
        )}

        {/* Referencia */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
            Referencia / Nota (opcional)
          </Label>
          <Textarea
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="Ej: Transferencia BBVA 24/03, monto $2,500..."
            rows={2}
            className="text-sm"
          />
        </div>

        {/* Drop zone */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
            Archivo <span className="text-gray-400 font-normal">(PDF, JPG, PNG, WEBP — máx. 20 MB)</span>
          </Label>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-lg transition-colors ${
              isDragging
                ? 'border-blue-400 bg-blue-50'
                : selectedFile
                ? 'border-green-400 bg-green-50'
                : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
            {selectedFile ? (
              <div className="p-4 flex items-center gap-4">
                {previewUrl ? (
                  <img src={previewUrl} alt="preview" className="h-20 w-20 object-cover rounded-md border" />
                ) : (
                  <div className="h-20 w-20 flex items-center justify-center bg-red-50 rounded-md border border-red-100">
                    <FileText className="w-10 h-10 text-red-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(selectedFile.size)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreviewUrl(null); }}
                  className="p-1.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Upload className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">
                  Arrastra tu comprobante aquí o <span className="text-blue-600 font-medium">haz clic para seleccionar</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {uploadMutation.isError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-sm text-red-700">{(uploadMutation.error as Error)?.message ?? 'Error al subir el comprobante'}</p>
          </div>
        )}

        <Button
          onClick={() => uploadMutation.mutate()}
          disabled={!canUpload || uploadMutation.isPending}
          className="w-full"
        >
          {uploadMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Enviar Comprobante</>
          )}
        </Button>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-800">Historial de Comprobantes</h2>
        </div>
        {loadingProofs ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Cargando...</p>
          </div>
        ) : myProofs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Clock className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">No has subido comprobantes aún</p>
          </div>
        ) : (
          <ul className="divide-y">
            {myProofs.map((proof) => {
              const statusCfg = STATUS_CONFIG[proof.status] || STATUS_CONFIG.pendiente;
              return (
                <li key={proof.id} className="px-6 py-4 flex items-center gap-4">
                  <FileTypeIcon tipo={proof.archivoTipo} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{proof.archivoNombre}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(proof.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {proof.invoiceNumber && <> · {proof.invoiceNumber}</>}
                      {proof.referencia && <> · {proof.referencia}</>}
                    </p>
                    {proof.notasAdmin && (
                      <p className="text-xs text-gray-400 mt-0.5 italic">"{proof.notasAdmin}"</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${statusCfg.className}`}>
                    {statusCfg.label}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={`/api/payment-proofs/${proof.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-blue-600"
                      title="Ver archivo"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                    {proof.status === 'pendiente' && (
                      <button
                        onClick={() => deleteMutation.mutate(proof.id)}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
